import { useContext, useEffect, useRef, useState } from 'react';
import {
  getCurrencyFormat,
  formatUnixTime,
  LabelledItemShape,
  parseId,
  LabelledItemList,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import { WarrantyStatus } from '@rabbit/data/types';
import { ConsumerHoldingSummaryShape } from '@rabbit/bizproc/client';
import {
  getProductTitleFromHoldingData,
  stripTags,
  getRetailerLabel,
} from '../utils/helpers';
import { useTranslation } from 'react-i18next';
import voidSVG from '../assets/images/Void.svg';
import { BL_Warranty } from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';
import { CaseflowContext } from '../context/CaseflowContext';
import { OurDateTime } from '@rabbit/utils/ts';
import { AdditionalFields, DisplayType } from '@rabbit/helper';

export function ClaimDetailPrint() {
  const { t } = useTranslation();
  const {
    caseFacts,
    caseUpdates,
    holdingData,
    holdingProxyData,
    caseId,
    caseTimeCreated,
    repairerCaseFacts,
    warrantorCasefacts,
  } = useContext(CaseflowContext) || {};
  const { tenantInfo, config } = useContext(AppContext);
  const tenantLink = t('tenantLink');
  const latestWarranty = BL_Warranty.getLatestWarranty(
    holdingProxyData?.warranties
  );

  const warrantyInfo = BL_Warranty.getLatestWarranty(holdingData?.warranties);
  const voided = warrantyInfo?.status === WarrantyStatus.VOIDED;
  const printRef = useRef<HTMLDivElement>(null);
  const [retailerLabel, setRetailerLabel] = useState<string>('');

  // Helper function to get purchase location from holding data
  const getPurchaseLocationFromHolding = (
    holdingData: ConsumerHoldingSummaryShape
  ): string => {
    const locationName = holdingData?.full_purchase_location?.name;
    const otherLocation = holdingData?.purchase_location_other;

    if (!locationName) {
      return otherLocation || '-';
    }

    const town =
      holdingData.full_purchase_location?.addresses?.[0]?.town ||
      holdingData.full_purchase_location?.address?.town;

    return town ? `${locationName} - ${town}` : locationName;
  };

  // Fetch retailer label when consumer_holding_purchase_location is available
  useEffect(() => {
    const fetchRetailerLabel = async () => {
      if (!caseFacts?.consumer_holding_purchase_location) {
        setRetailerLabel('');
        return;
      }

      const label = await getRetailerLabel(
        caseFacts.consumer_holding_purchase_location
      );
      setRetailerLabel(label || '');
    };

    fetchRetailerLabel();
  }, [caseFacts?.consumer_holding_purchase_location]);

  const getPurchaseLocation = (): string => {
    // Priority 1: Get from holdingData if available (most detailed)
    if (holdingData) {
      const locationFromHolding = getPurchaseLocationFromHolding(holdingData);
      if (locationFromHolding !== '-') {
        return locationFromHolding;
      }
    }

    // Priority 2: Get from fetched retailer label using consumer_holding_purchase_location
    if (retailerLabel) {
      return retailerLabel;
    }

    return '-';
  };
  const isWithinWarranty = () => {
    if (!holdingProxyData) return '-';

    if (!latestWarranty || !latestWarranty.endDate) return '-';

    return OurDateTime.nowUTCTimestamp() < latestWarranty?.endDate
      ? 'Yes'
      : 'No';
  };

  useEffect(() => {
    if (holdingData) {
      // Delay the print to allow logo to load
      setTimeout(() => {
        window.print();
      }, 1000);
    }
  }, [holdingData]);

  const productItems: LabelledItemShape[] = holdingData
    ? [
        {
          label: t('general.id'),
          value: holdingProxyData?.docid?.slice(0, 5).toUpperCase() ?? '-',
        },
        {
          label: t('general.product'),
          value: getProductTitleFromHoldingData(holdingData),
        },
        {
          label: t('general.purchaseLocation'),
          value: getPurchaseLocation(),
        },
        {
          label: t('general.purchasePrice'),
          value: getCurrencyFormat(
            caseFacts?.consumer_holding_purchase_price
              ? (caseFacts?.consumer_holding_purchase_price
                  .amount as unknown as string)
              : '0',
            caseFacts?.consumer_holding_purchase_price?.currency ??
              tenantInfo?.currency ??
              'GBP'
          ),
        },
        ...(config.CLAIMS.CLAIMS_FLOW.NEW_CLAIM.SERIAL_NUMBER
          ? [
              {
                label: t('general.serialNumber'),
                value:
                  (caseFacts?.consumer_holding_serial_number
                    ? caseFacts.consumer_holding_serial_number
                    : caseFacts?.serial_number
                    ? caseFacts.serial_number
                    : '-') + ' ',
                className: 'whitespace-nowrap',
              },
            ]
          : []),
      ]
    : [];

  const claimDetailItems: LabelledItemShape[] = [
    {
      label: t('general.claimId'),
      value: caseId
        ? `${
            caseFacts?.warranty_status
              ? caseFacts?.warranty_status.toLowerCase() === 'in warranty'
                ? 'WR-'
                : 'OR-'
              : ''
          }${parseId(caseId)}`
        : '-',
    },
    {
      label: t('general.dateOfClaim'),
      value: caseTimeCreated
        ? formatUnixTime(Number(caseTimeCreated), 'dd/MM/yyyy')
        : '-',
    },
    {
      label: t('general.purchaseDate'),
      value:
        holdingData?.purchase_time && holdingData?.purchase_time > 0
          ? formatUnixTime(holdingData?.purchase_time, 'dd/MM/yyyy')
          : '-',
    },
    {
      label: t('general.warrantyTerm'),
      value: holdingData?.warranty_term?.amount
        ? `${holdingData?.warranty_term?.amount} ${holdingData?.warranty_term?.division}`
        : '-',
    },
    {
      label: t('general.warrantyExpiry'),
      value:
        latestWarranty?.endDate && latestWarranty?.endDate > 0
          ? formatUnixTime(latestWarranty?.endDate, 'dd/MM/yyyy')
          : '-',
    },
    {
      label: t('general.withinWarrantyPeriod'),
      value: isWithinWarranty(),
    },
    {
      label: t('general.finalAssessment'),
      value: caseFacts?.final_assessment ?? '-',
    },
    {
      label: t('general.additionalComment'),
      value: (
        <div
          dangerouslySetInnerHTML={{
            __html: caseFacts?.assessment_comment ?? '-',
          }}
        ></div>
      ),
      className: 'col-span-full',
    },
    {
      label: t('general.serialNumberProof'),
      value: (
        <div className="w-full">
          {caseFacts?.serial_number_proof?.map((i) => (
            <img src={i.url} className="max-h-[400px] w-full object-contain" />
          ))}
        </div>
      ),
      className: 'col-span-full',
    },
  ];
  const consumerItems: LabelledItemShape[] = [
    {
      label: t('general.name'),
      value: caseFacts?.consumer_name ?? '-',
    },
    ...(tenantLink !== 'ISCOOT'
      ? [
          {
            label: t('general.email'),
            value: caseFacts?.consumer_email ?? '-',
          },
        ]
      : []),
    {
      label: t('general.issueType'),
      value: caseFacts?.consumer_issue_type ?? '-',
    },
    {
      label: t('general.issueDescription'),
      value: stripTags(caseFacts?.consumer_issue_description ?? '') ?? '-',
      className: 'col-span-full',
    },
  ];

  // Helper function to format keys (e.g., "firstName" -> "First Name")
  const formatKey = (key: string) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/(?:^|\s)\S/g, (a) => a.toUpperCase());
  };

  // Helper function to transform keys in data object
  const transformKeys = (data: { [key: string]: any }) => {
    const newData: { [key: string]: any } = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const formattedKey = formatKey(key);
        newData[formattedKey] = data[key];
      }
    }
    return newData;
  };

  // Helper function to filter fields by display settings
  const filterFieldsByDisplaySettings = (
    fields: { [key: string]: any },
    fieldsConfig: AdditionalFields,
    isPartnerToTenant: boolean,
    isCustomerPersona: boolean
  ): { [key: string]: any } => {
    return Object.entries(fields)
      .filter(([key, _]) => {
        const fieldConfig = fieldsConfig[key];
        if (
          !fieldConfig ||
          !fieldConfig.display ||
          fieldConfig.display.length === 0
        ) {
          return true;
        }

        return (
          (isCustomerPersona &&
            fieldConfig.display.includes(DisplayType.DISPLAY_FOR_CONSUMER)) ||
          fieldConfig.display.includes(
            isPartnerToTenant
              ? DisplayType.DISPLAY_FOR_PARTNERS
              : DisplayType.DISPLAY_FOR_TENANT
          )
        );
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as { [key: string]: any });
  };

  // Additional Details items for Supagard
  const additionalDetailsItems: LabelledItemShape[] =
    tenantLink === 'SUPAGARD' &&
    config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS &&
    holdingProxyData?.additionalData &&
    Object.keys(holdingProxyData.additionalData).length > 0
      ? (() => {
          const transformedAdditionalData = transformKeys(
            filterFieldsByDisplaySettings(
              holdingProxyData.additionalData,
              config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS,
              false, // isPartnerToTenant - for print, we show tenant view
              false // isCustomerPersona - for print, we show tenant view
            ) || {}
          );

          return Object.entries(transformedAdditionalData).map(
            ([label, value]) => ({
              label,
              value:
                typeof value === 'boolean'
                  ? value
                    ? 'Yes'
                    : 'No'
                  : value?.toString() || '-',
            })
          );
        })()
      : [];

  const comments: LabelledItemShape[] = (caseUpdates || [])
    .sort((a, b) => b.t - a.t)
    .filter((i: any) => i.kv?.internal_comment)
    .map((comment: any) => ({
      label: `${formatUnixTime(comment.t, 'dd/MM/yyyy')}`,
      value: (
        <div
          dangerouslySetInnerHTML={{
            __html: comment.kv?.internal_comment?.comment ?? '-',
          }}
        ></div>
      ),
      className: 'col-span-full',
    }));

  return (
    <div className="flex w-[800px] flex-col gap-3 py-6 " ref={printRef}>
      {(!holdingData || !holdingProxyData) && (
        <LoadingSpinner size="sm" overlay={true} />
      )}
      {voided && (
        <div className="fixed left-0 top-0 z-10 flex h-full w-full items-center justify-center">
          <img src={voidSVG} alt="void" className="-mt-[200px] w-[400px]" />
        </div>
      )}
      <div className="flex items-start justify-between">
        <div className="font-caudex text-2xl">Claim Summary</div>
        <div className="flex flex-col items-end justify-end gap-2 text-gray-600">
          <img
            src={tenantInfo?.logo}
            alt={tenantInfo?.name}
            className="mr-1 max-h-[40px] max-w-[220px]"
          />
          <div>{t('CFG_COBRAND_EMAIL')}</div>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {[
          { label: 'Customer detail', items: consumerItems },
          { label: 'Product detail', items: productItems },
          { label: 'Claim detail', items: claimDetailItems },
          ...(additionalDetailsItems.length > 0
            ? [
                {
                  label: t('general.additionalDetails'),
                  items: additionalDetailsItems,
                },
              ]
            : []),
          { label: 'Comments', items: comments },
        ].map(
          (item, index) =>
            item.items && (
              <div
                className="rounded-md border border-gray-300 bg-white py-2 px-3"
                key={'print-item-' + index}
              >
                <div>
                  <div className="font-caudex mb-2 border-b border-gray-300 pb-2 text-lg">
                    {item.label}
                  </div>
                  <LabelledItemList items={item.items} />
                </div>
              </div>
            )
        )}
      </div>
      <div className="break-after-page print:w-full">&nbsp;</div>
      <div className="mt-[200px]">
        {[
          ...(caseFacts?.consumer_proof_of_purchase || []),
          ...(caseFacts?.consumer_claim_evidence || []),
          ...(repairerCaseFacts?.assessment_attachment || []),
          ...(warrantorCasefacts?.assessment_attachment || []),
        ].map((file) => {
          return (
            <div className="mt-4 block w-full break-after-page border-2 border-gray-400">
              <div className="font-caudex mb-2 text-lg">{file.ogFilename}</div>
              <img
                src={file.url}
                className="block w-full object-contain print:max-h-[90vh] print:w-full"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ClaimDetailPrint;
